import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAvailableThinkers,
  useSetConversationThinkers,
  useStartConversation,
} from "~/api/queries";
import { ThinkerPicker } from "~/features/thinkers/ThinkerPicker";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { Text } from "~/foundation/components/Text";

/** A client-minted id, the way the platform accepts one. S10 mints the SAME id for a thread. */
function mintConversationId(): string {
  const uuid =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `instruction-${uuid}`;
}

/**
 * New conversation (W8 §B.2).
 *
 * The id is minted HERE and sent, which is what the server accepts and what S10's identity
 * fusion depends on: a comment thread's id IS its conversation's id, and there is no join
 * table anywhere in the design. Thinkers are chosen through W1's `ThinkerPicker` — kms loaded
 * them from a proxied path with no local route (W8-25), and there is no second picker here.
 */
export function ConversationCreateDialog({
  open,
  onOpenChange,
  documentId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string;
  onCreated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [thinkerIds, setThinkerIds] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const start = useStartConversation();
  const setThinkers = useSetConversationThinkers();
  const { data: thinkers } = useAvailableThinkers();

  const names = thinkerIds.map(
    (id) => (thinkers ?? []).find((thinker) => thinker.id === id)?.name ?? id,
  );

  const create = () => {
    const id = mintConversationId();
    start.mutate(
      { id, documentId, title: title.trim() || t("New conversation") },
      {
        onSuccess: (created) => {
          if (thinkerIds.length > 0) {
            setThinkers.mutate({ id: created.id, thinkerIds });
          }
          setTitle("");
          setThinkerIds([]);
          onOpenChange(false);
          onCreated(created.id);
        },
      },
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        title={t("New conversation")}
        closeLabel={t("Close")}
        size="sm"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {t("Cancel")}
            </Button>
            <Button disabled={start.isPending} onClick={create}>
              {start.isPending ? t("Creating…") : t("Create")}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-small text-text-secondary">{t("Title")}</span>
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={t("What is this conversation about?")}
              className="h-8 rounded-sm border border-input-border bg-bg px-2 text-body text-text focus:outline-none"
            />
          </label>

          <div className="flex flex-col gap-1">
            <span className="text-small text-text-secondary">{t("AI thinkers")}</span>
            <Button variant="neutral" size="sm" onClick={() => setPickerOpen(true)}>
              {names.length === 0 ? t("Choose thinkers") : names.join(", ")}
            </Button>
            <Text variant="small" className="text-text-tertiary">
              {t("Thinkers answer in the order you choose them.")}
            </Text>
          </div>
        </div>
      </Dialog>

      <ThinkerPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        selected={thinkerIds}
        onSelect={setThinkerIds}
        multiple
        filter="ai"
        documentId={documentId}
        title={t("Add thinkers to this conversation")}
        confirmLabel={t("Use these thinkers")}
      />
    </>
  );
}
